import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    Validators,
} from '@angular/forms';
import {
    ActivatedRoute,
    Router,
} from '@angular/router';
import {
    ITreeItemFlatNode,
    TreeChecklistComponent,
} from '@components/ui-tree-checklist/ui-tree-checklist.component';
import {
    ClientErrorCodes,
    IWebhook,
    TreeDataGroup,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import { Logger } from '@services';
import { WebhookService } from '@services/webhook.service';
import { UiSnackBarService } from '@uipath/angular/components/ui-snackbar';
import UiValidators, { WEBHOOKS_SECRET_MAX_LENGTH } from '@validators';

import cloneDeep from 'lodash-es/cloneDeep';
import {
    BehaviorSubject,
    Observable,
    of,
    Subject,
    throwError,
} from 'rxjs';
import {
    debounceTime,
    filter,
    finalize,
    map,
    startWith,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs/operators';

export enum WebHookFormFields {
    id = 'id',
    enabled = 'enabled',
    url = 'url',
    allowInsecureSSL = 'allowInsecureSSL',
    secret = 'secret',
    subscribeToAllEvents = 'subscribeToAllEvents',
    events = 'events',
}

@Component({
    templateUrl: './webhook.upsert.page.html',
    styleUrls: ['./webhook.upsert.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WebhookUpsertPageComponent implements OnInit, OnDestroy {
    public eventGroups$: Observable<TreeDataGroup[]>;
    public formGroup?: FormGroup;
    public preselectedNodes?: { [key: string]: string[] };

    public loading$ = new BehaviorSubject(true);
    public cantConnect$ = new BehaviorSubject(false);
    public badSSL$ = new BehaviorSubject(false);
    public isEditMode = false;
    public fields = WebHookFormFields;

    @ViewChild(TreeChecklistComponent, { static: false })
    eventsTree?: TreeChecklistComponent;

    private _webhook: IWebhook = {
        id: NaN,
        url: '',
        enabled: true,
        allowInsecureSSL: false,
        subscribeToAllEvents: true,
        events: [],
    };

    private _destroyed$ = new Subject<void>();

    constructor(
        private _webhookService: WebhookService,
        private _translateService: TranslateService,
        private _fb: FormBuilder,
        private _router: Router,
        private _route: ActivatedRoute,
        private _logger: Logger,
        private _snack: UiSnackBarService,
    ) {
        this.eventGroups$ = this._webhookService.getEventTypeGroups();
    }

    ngOnInit() {
        this._contextData$
            .pipe(
                tap(() => this.loading$.next(false)),
                tap(this._initForm),
                takeUntil(this._destroyed$),
            )
            .subscribe(
                () => { },
                (e: any) => {
                    if (typeof e === 'string') {
                        this._logger.error(e);
                    }
                    this._logger.error(e);
                    this._router.navigateByUrl('/webhooks');
                },
            );
    }

    ngOnDestroy() {
        this._destroyed$.next();
    }

    public clipboardCopied() {
        this._snack.success(this._translateService.instant('global_message_copied_clipboard'));
    }

    public onSelectEvent(selectedNodes: ITreeItemFlatNode[]) {
        this.formGroup!.get(WebHookFormFields.events)!.setValue(
            selectedNodes
                .filter(node => node.level !== 0)
                .map(node => node.item),
        );
    }

    public onSubmit() {
        if (
            this.formGroup!.invalid ||
            this.loading$.value
        ) { return; }
        this.loading$.next(true);

        const payload: Partial<IWebhook> = cloneDeep(this.formGroup!.value);
        payload.id = payload.id ? payload.id : void 0;
        payload.events = payload.subscribeToAllEvents ? [] : payload.events;

        (this.isEditMode ?
            this._webhookService.update(this._webhook.id, payload as IWebhook) :
            this._webhookService.add(payload as IWebhook)
        ).pipe(
            finalize(
                () => this.loading$.next(false),
            ),
        ).subscribe(
            () => this._router.navigate(['/webhooks']),
        );
    }

    private get _contextData$() {
        const id = this._route.snapshot.paramMap.get('id')!;
        this.isEditMode = id !== null;

        if (!this.isEditMode) {
            return of(this._webhook);
        }

        const webhookId = parseInt(id, 10);

        if (!(webhookId > 0)) {
            return throwError(`${this._translateService.instant('invalid_webhook_ID')} (#${ClientErrorCodes.InvalidWebHookID})`);
        }

        return this._webhookService
            .getInstance(webhookId)
            .pipe(
                tap(model => this._webhook = model),
            );

    }

    private get _editGroups() {
        return this._webhook.events.reduce(
            (accumulator, current) => {
                const [entity] = current.split('.');

                accumulator[entity] = [...accumulator[entity] || [], current];

                return accumulator;
            }, {} as { [key: string]: string[] },
        );
    }

    private _initForm = (webhook: IWebhook) => {
        const formFields = {
            [WebHookFormFields.id]: [
                webhook.id,
            ],
            [WebHookFormFields.enabled]: [
                webhook.enabled,
            ],
            [WebHookFormFields.url]: [
                webhook.url,
                {
                    validators: [
                        Validators.required,
                        UiValidators.httpUrl(false),
                    ],
                },
            ],
            [WebHookFormFields.allowInsecureSSL]: [
                webhook.allowInsecureSSL,
            ],
            [WebHookFormFields.secret]: [
                webhook.secret,
                Validators.compose([
                    Validators.maxLength(WEBHOOKS_SECRET_MAX_LENGTH),
                ]),
            ],
            [WebHookFormFields.subscribeToAllEvents]: [
                webhook.subscribeToAllEvents,
                Validators.compose([
                    Validators.required,
                ]),
            ],
            [WebHookFormFields.events]: [
                webhook.events,
            ],
        };

        this.formGroup = this._fb.group(formFields, {
            validator: this._selectedEventsValidator,
        });

        this._urlOnChangeValidator$()
            .subscribe(response => {
                this.cantConnect$.next(!response.canConnect);
                this.badSSL$.next(!!response.hasBadSsl);
            });
        this.preselectedNodes = this._editGroups;
    }

    private _urlOnChangeValidator$() {
        return this.formGroup!.get(WebHookFormFields.url)!
            .valueChanges
            .pipe(
                startWith(this.formGroup!.get(WebHookFormFields.url)!.value),
                debounceTime(300),
                tap(() => {
                    this.cantConnect$.next(false);
                    this.badSSL$.next(false);
                }),
                filter(() => !this.formGroup!.get(WebHookFormFields.url)!.invalid),
                filter(val => !!val),
                map(val => val.trim()),
                switchMap(val => this._webhookService.verifyHost(val)),
                takeUntil(this._destroyed$),
            );
    }

    private _selectedEventsValidator = (form: FormGroup) => {
        if (
            !form.get(WebHookFormFields.subscribeToAllEvents)!.value &&
            form.get(WebHookFormFields.events)!.value.length === 0
        ) {
            return {
                noSelectedEvents: true,
            };
        }
        return null;
    }

}
